import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { courseAPI, learningAssetAPI } from '../services/api';
import AssetRenderer from '../components/AssetRenderer';
import AIChatAssistant from '../components/AIChatAssistant';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';
import { Skeleton } from '@mui/material';

function CourseContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const contentRef = useRef(null);
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [assets, setAssets] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedChapters, setExpandedChapters] = useState({});
  const [isStudyMode, setIsStudyMode] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showStudyModeHint, setShowStudyModeHint] = useState(false);
  const [progressJustUpdated, setProgressJustUpdated] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Load dark mode preference from localStorage
    const savedDarkMode = localStorage.getItem('studyModeDarkMode');
    return savedDarkMode === 'true';
  });
  const [chapterOrder, setChapterOrder] = useState(() => {
    // Load chapter order preference from localStorage (course-specific)
    const savedOrder = localStorage.getItem(`courseContent_chapterSortOrder_${id}`);
    return savedOrder || 'old-new'; // Default to oldest first
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);

  const getId = (value) => (value && value._id ? value._id : value) || null;

  const buildOrderedAssets = (allAssets, allChapters, order = 'old-new') => {
    const assetsByChapter = new Map();
    allAssets.forEach(asset => {
      const chapterId = getId(asset.chapter) || 'root';
      if (!assetsByChapter.has(chapterId)) {
        assetsByChapter.set(chapterId, []);
      }
      assetsByChapter.get(chapterId).push(asset);
    });

    const chaptersByParent = new Map();
    allChapters.forEach(chapter => {
      const parentId = getId(chapter.parentChapter) || 'root';
      if (!chaptersByParent.has(parentId)) {
        chaptersByParent.set(parentId, []);
      }
      chaptersByParent.get(parentId).push(chapter);
    });

    // Sort chapters by creation date
    const sortChaptersByDate = (a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return order === 'old-new' ? dateA - dateB : dateB - dateA;
    };

    // Always sort assets by order field (ascending)
    const sortAssetsByOrder = (a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    };

    const ordered = [];

    const appendAssetsForChapter = (chapterId) => {
      const chapterAssets = assetsByChapter.get(chapterId) || [];
      chapterAssets.sort(sortAssetsByOrder);
      ordered.push(...chapterAssets);
    };

    const walk = (parentId) => {
      const children = chaptersByParent.get(parentId) || [];
      children.sort(sortChaptersByDate);
      children.forEach(chapter => {
        const chapterId = getId(chapter);
        appendAssetsForChapter(chapterId);
        walk(chapterId);
      });
    };

    walk('root');

    const unassigned = assetsByChapter.get('root') || [];
    if (unassigned.length > 0) {
      unassigned.sort(sortAssetsByOrder);
      ordered.push(...unassigned);
    }

    return ordered;
  };

  const orderedAssets = useMemo(() => buildOrderedAssets(assets, chapters, chapterOrder), [assets, chapters, chapterOrder]);

  const fetchCourseContent = async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getCourse(id);
      const courseData = response.data;
      setCourse(courseData.course);
      setChapters(courseData.chapters || []);
      setAssets(courseData.assets || []);
      setEnrollment(courseData.enrollment || null);
      
      // Expand all chapters by default
      const expanded = {};
      (courseData.chapters || []).forEach(ch => {
        expanded[ch._id] = true;
      });
      setExpandedChapters(expanded);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load course content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCourseContent();
    }
    
    // Cleanup: clear study mode from localStorage when leaving the course
    return () => {
      localStorage.removeItem('courseStudyMode');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // Save study mode state to localStorage for Navbar
    localStorage.setItem('courseStudyMode', isStudyMode.toString());
  }, [isStudyMode]);

  useEffect(() => {
    // Save dark mode preference to localStorage
    localStorage.setItem('studyModeDarkMode', isDarkMode.toString());
  }, [isDarkMode]);

  useEffect(() => {
    // Save chapter order preference to localStorage (course-specific)
    if (id) {
      localStorage.setItem(`courseContent_chapterSortOrder_${id}`, chapterOrder);
    }
  }, [chapterOrder, id]);

  useEffect(() => {
    // Update selected asset when URL parameter changes (browser back/forward)
    const assetIdFromUrl = searchParams.get('asset');
    if (assetIdFromUrl && assets.length > 0) {
      const assetFromUrl = assets.find(a => a._id === assetIdFromUrl);
      if (assetFromUrl && (!selectedAsset || selectedAsset._id !== assetIdFromUrl)) {
        setSelectedAsset(assetFromUrl);
        if (contentRef.current) {
          contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, assets]);

  useEffect(() => {
    // Close sidebar when exiting study mode
    if (!isStudyMode) {
      setIsSidebarOpen(false);
      // Show hint when exiting study mode for the first time
      setShowStudyModeHint(true);
      // Auto-hide hint after 5 seconds
      const timer = setTimeout(() => {
        setShowStudyModeHint(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isStudyMode]);

  useEffect(() => {
    // Override body background in study mode
    if (isStudyMode) {
      const originalBackground = document.body.style.background;
      document.body.style.background = isDarkMode ? '#1a202c' : '#ffffff';
      
      return () => {
        document.body.style.background = originalBackground;
      };
    }
  }, [isStudyMode, isDarkMode]);

  const handleAssetComplete = async (assetId, score = null, timeSpent = null) => {
    // Prevent completion actions in admin preview mode (no enrollment)
    if (!enrollment) {
      setToast({ 
        show: true, 
        message: 'Preview Mode: Completion tracking not available', 
        type: 'info' 
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
      return;
    }
    
    // Check if asset is already completed
    const isCompleted = isAssetCompleted(assetId);
    
    try {
      if (isCompleted) {
        // Mark as incomplete
        console.log('Marking asset incomplete:', assetId);
        const response = await learningAssetAPI.markAssetIncomplete(assetId);
        console.log('Asset marked incomplete:', response.data);
        
        // Show success toast
        setToast({ show: true, message: 'Marked as incomplete!', type: 'success' });
      } else {
        // Mark as complete
        console.log('Marking asset complete:', assetId);
        const response = await learningAssetAPI.markAssetComplete(assetId, score, timeSpent);
        console.log('Asset marked complete:', response.data);
        
        // Show success toast
        setToast({ show: true, message: 'Marked as complete!', type: 'success' });
      }
      
      // Refresh enrollment to update progress - force re-render with deep clone
      const courseResponse = await courseAPI.getCourse(id);
      const updatedEnrollment = courseResponse.data.enrollment;
      
      if (updatedEnrollment) {
        // Deep clone to ensure React detects the change
        setEnrollment({
          ...updatedEnrollment,
          progress: updatedEnrollment.progress ? { ...updatedEnrollment.progress } : null
        });
        console.log('Enrollment updated:', updatedEnrollment);
        
        // Trigger progress update animation
        setProgressJustUpdated(true);
        setTimeout(() => setProgressJustUpdated(false), 1000);
      }
      
      // Auto-hide toast after 3 seconds
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Error toggling asset completion:', err);
      console.error('Error details:', err.response?.data);
      const errorMessage = isCompleted 
        ? 'Failed to mark as incomplete' 
        : 'Failed to mark as complete';
      setToast({ show: true, message: err.response?.data?.message || errorMessage, type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 3000);
    }
  };

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterId]: !prev[chapterId]
    }));
  };

  const isAssetCompleted = (assetId) => {
    if (!enrollment || !enrollment.progress || !enrollment.progress.completedAssets) return false;
    return enrollment.progress.completedAssets.some(ca => 
      ca.assetId === assetId || ca.assetId?._id === assetId
    );
  };

  const getCurrentAssetIndex = () => {
    if (!selectedAsset || !orderedAssets) return -1;
    return orderedAssets.findIndex(a => a._id === selectedAsset._id);
  };

  const navigateToNextAsset = () => {
    const currentIndex = getCurrentAssetIndex();
    if (currentIndex >= 0 && currentIndex < orderedAssets.length - 1) {
      const nextAsset = orderedAssets[currentIndex + 1];
      setSelectedAsset(nextAsset);
      setSearchParams({ asset: nextAsset._id });
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const navigateToPreviousAsset = () => {
    const currentIndex = getCurrentAssetIndex();
    if (currentIndex > 0) {
      const prevAsset = orderedAssets[currentIndex - 1];
      setSelectedAsset(prevAsset);
      setSearchParams({ asset: prevAsset._id });
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const hasNextAsset = () => {
    const currentIndex = getCurrentAssetIndex();
    return currentIndex >= 0 && currentIndex < orderedAssets.length - 1;
  };

  const hasPreviousAsset = () => {
    const currentIndex = getCurrentAssetIndex();
    return currentIndex > 0;
  };

  const getChapterName = (chapterId) => {
    if (!chapterId) return null;
    const chapter = chapters.find(ch => ch._id === chapterId || ch._id === chapterId._id);
    return chapter ? chapter.title : null;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getVideoDuration = (assetId) => {
    // First, try to get duration from the asset itself
    const asset = assets.find(a => a._id === assetId);
    if (asset && asset.estimatedDuration) {
      // estimatedDuration is in minutes, convert to seconds
      return asset.estimatedDuration * 60;
    }
    
    // Fallback to video progress data (actual played duration)
    if (enrollment && enrollment.progress && enrollment.progress.videoProgress) {
      const videoProgress = enrollment.progress.videoProgress.find(
        vp => vp.assetId === assetId || vp.assetId?._id === assetId
      );
      if (videoProgress?.totalDuration) {
        return videoProgress.totalDuration;
      }
    }
    
    return null;
  };

  const renderChapterNav = (chapter, level = 0) => {
    const chapterAssets = assets
      .filter(a => a.chapter?._id === chapter._id || a.chapter === chapter._id)
      .sort((a, b) => {
        const orderA = a.order ?? 0;
        const orderB = b.order ?? 0;
        return orderA - orderB; // Always sort assets by order field ascending
      });
    const subChapters = (chapter.subChapters || []).slice().sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return chapterOrder === 'old-new' ? dateA - dateB : dateB - dateA;
    });
    const isExpanded = expandedChapters[chapter._id];

    return (
      <div key={chapter._id} style={{ marginLeft: `${level * 16}px` }}>
        <div
          onClick={() => toggleChapter(chapter._id)}
          style={{
            padding: '8px 12px',
            cursor: 'pointer',
            background: isExpanded ? '#2d3748' : 'transparent',
            borderRadius: '6px',
            marginBottom: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px' }}>{isExpanded ? '▼' : '▶'}</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
              {level === 0 ? '📖' : '📄'} {chapter.title}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div style={{ marginLeft: '16px', marginTop: '4px' }}>
            {chapterAssets.map(asset => (
              <div
                key={asset._id}
                onClick={() => {
                  setSelectedAsset(asset);
                  setSearchParams({ asset: asset._id });
                  if (isStudyMode) setIsSidebarOpen(false);
                  if (contentRef.current) {
                    contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                style={{
                  padding: '6px 12px',
                  cursor: 'pointer',
                  background: selectedAsset?._id === asset._id ? '#667eea' : 'transparent',
                  borderRadius: '6px',
                  marginBottom: '2px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ 
                    fontSize: '16px', 
                    color: isAssetCompleted(asset._id) ? '#48bb78' : '#718096',
                    lineHeight: '1'
                  }}>
                    ●
                  </span>
                  <span style={{ fontSize: '14px' }}>
                    {asset.assetType === 'video' && '▶️'}
                    {asset.assetType === 'document' && '📄'}
                    {asset.assetType === 'quiz' && '📝'}
                    {asset.assetType === 'flashcard' && '🎴'}
                    {asset.assetType === 'interview' && '🎤'}
                    {asset.assetType === 'link' && '🔗'}
                    {asset.assetType === 'note' && '📋'}
                  </span>
                  <span style={{ 
                    fontSize: '13px', 
                    color: selectedAsset?._id === asset._id ? '#fff' : '#a0aec0' 
                  }}>
                    {asset.title}
                  </span>
                </div>
              </div>
            ))}
            
            {subChapters.map(sub => renderChapterNav(sub, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', height: 'calc(100vh - 60px)', background: '#f7fafc' }}>
        {/* Sidebar Skeleton */}
        <div style={{
          width: '320px',
          background: '#1a202c',
          borderRight: '1px solid #2d3748',
          padding: '20px'
        }}>
          {/* Back button skeleton */}
          <Skeleton variant="rectangular" width={80} height={32} sx={{ bgcolor: '#2d3748', borderRadius: '6px', marginBottom: '16px' }} />
          
          {/* Course title skeleton */}
          <Skeleton variant="text" width="90%" height={28} sx={{ bgcolor: '#2d3748', marginBottom: '8px' }} />
          <Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: '#2d3748', marginBottom: '16px' }} />
          
          {/* Progress bar skeleton */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <Skeleton variant="text" width={60} height={16} sx={{ bgcolor: '#2d3748' }} />
              <Skeleton variant="text" width={30} height={16} sx={{ bgcolor: '#2d3748' }} />
            </div>
            <Skeleton variant="rectangular" width="100%" height={8} sx={{ bgcolor: '#2d3748', borderRadius: '4px' }} />
          </div>
          
          {/* Chapter order selector skeleton */}
          <Skeleton variant="rectangular" width="100%" height={36} sx={{ bgcolor: '#2d3748', borderRadius: '6px', marginBottom: '20px' }} />
          
          {/* Chapter list skeleton */}
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ marginBottom: '16px' }}>
              <Skeleton variant="rectangular" width="100%" height={44} sx={{ bgcolor: '#2d3748', borderRadius: '8px', marginBottom: '8px' }} />
              {i === 1 && (
                <div style={{ paddingLeft: '16px' }}>
                  <Skeleton variant="rectangular" width="90%" height={36} sx={{ bgcolor: '#374151', borderRadius: '6px', marginBottom: '6px' }} />
                  <Skeleton variant="rectangular" width="90%" height={36} sx={{ bgcolor: '#374151', borderRadius: '6px', marginBottom: '6px' }} />
                  <Skeleton variant="rectangular" width="90%" height={36} sx={{ bgcolor: '#374151', borderRadius: '6px' }} />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Main content skeleton */}
        <div style={{ flex: 1, padding: '40px', overflow: 'auto' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {/* Asset title skeleton */}
            <Skeleton variant="text" width="70%" height={48} sx={{ marginBottom: '16px' }} />
            
            {/* Asset type badge skeleton */}
            <Skeleton variant="rectangular" width={120} height={28} sx={{ borderRadius: '14px', marginBottom: '24px' }} />
            
            {/* Content area skeleton */}
            <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: '12px', marginBottom: '16px' }} />
            <Skeleton variant="text" width="90%" height={24} sx={{ marginBottom: '8px' }} />
            <Skeleton variant="text" width="85%" height={24} sx={{ marginBottom: '8px' }} />
            <Skeleton variant="text" width="75%" height={24} />
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#1a202c' }}>Course not found</div>;
  }

  // Allow admins to preview without enrollment
  if (!enrollment && user?.role !== 'admin') {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#1a202c' }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</p>
        <h2 style={{ fontSize: '24px', marginBottom: '12px' }}>Not Enrolled</h2>
        <p style={{ color: '#718096', marginBottom: '20px' }}>
          You are not enrolled in this course. Contact your instructor.
        </p>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/my-courses')}
        >
          Back to My Courses
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: isStudyMode ? '100vh' : 'calc(100vh - 60px)', background: isStudyMode ? (isDarkMode ? '#1a202c' : '#ffffff') : '#f7fafc', position: 'relative' }}>
      {/* Sidebar Navigation - Always rendered but conditionally styled */}
      <div style={{
        width: isStudyMode && !isSidebarOpen ? '0' : '320px',
        background: '#1a202c',
        borderRight: '1px solid #2d3748',
        overflow: isStudyMode && !isSidebarOpen ? 'hidden' : 'auto',
        padding: isStudyMode && !isSidebarOpen ? '0' : '20px',
        transition: 'all 0.3s ease',
        position: isStudyMode ? 'fixed' : 'static',
        left: 0,
        top: isStudyMode ? '0' : '60px',
        height: isStudyMode ? '100vh' : 'auto',
        zIndex: isStudyMode ? 999 : 'auto',
        boxShadow: isStudyMode && isSidebarOpen ? '4px 0 12px rgba(0,0,0,0.15)' : 'none'
      }}>
          {/* Course Header */}
          <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #2d3748' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  // Admins go back to course builder, students go to their courses
                  if (user?.role === 'admin') {
                    navigate(`/course-builder/${id}`);
                  } else {
                    navigate('/my-courses');
                  }
                }}
                style={{
                  padding: '6px 12px',
                  background: '#4a5568',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '12px',
                  whiteSpace: 'nowrap'
                }}
                title={user?.role === 'admin' ? 'Back to Course Builder' : 'Back to My Courses'}
              >
                ← Back
              </button>
              
              {!isStudyMode && (
                <button
                  onClick={() => setIsStudyMode(true)}
                  style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 'fit-content'
                  }}
                >
                  🎯 Study Mode
                </button>
              )}
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#e2e8f0', marginBottom: '8px' }}>
              {course.title}
            </h2>
            {user?.role === 'admin' && !enrollment && (
              <div style={{
                display: 'inline-block',
                padding: '4px 10px',
                background: '#667eea',
                color: '#ffffff',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                👁️ Preview Mode
              </div>
            )}
            <p style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '12px' }}>
              {course.code}
            </p>
            
            {/* Progress Bar */}
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: '#a0aec0' }}>Progress</span>
                <span style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#667eea',
                  transition: 'all 0.3s ease',
                  animation: progressJustUpdated ? 'progressPulse 0.6s ease-out' : 'none'
                }}>
                  {enrollment?.progress?.percentComplete || 0}%
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                background: '#2d3748',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${enrollment?.progress?.percentComplete || 0}%`,
                  height: '100%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  transition: 'width 0.5s ease',
                  boxShadow: progressJustUpdated ? '0 0 8px rgba(102, 126, 234, 0.6)' : 'none'
                }} />
              </div>
              <p style={{ 
                fontSize: '11px', 
                color: '#718096', 
                marginTop: '4px',
                transition: 'color 0.3s ease',
                animation: progressJustUpdated ? 'progressPulse 0.6s ease-out' : 'none'
              }}>
                {enrollment?.progress?.completedAssetsCount || 0} of {enrollment?.progress?.totalAssetsCount || 0} completed
              </p>
            </div>
          </div>

          {/* Chapter Navigation */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#a0aec0', textTransform: 'uppercase', margin: 0 }}>
                Course Content
              </h3>
              <select
                value={chapterOrder}
                onChange={(e) => setChapterOrder(e.target.value)}
                style={{
                  padding: '4px 8px',
                  background: 'transparent',
                  border: '1px solid #4a5568',
                  borderRadius: '4px',
                  color: '#a0aec0',
                  cursor: 'pointer',
                  fontSize: '11px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.color = '#667eea';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#4a5568';
                  e.currentTarget.style.color = '#a0aec0';
                }}
              >
                <option value="old-new">📅 Oldest First</option>
                <option value="new-old">🕐 Newest First</option>
              </select>
            </div>
            {chapters
              .filter(ch => !ch.parentChapter)
              .sort((a, b) => {
                const dateA = new Date(a.createdAt || 0);
                const dateB = new Date(b.createdAt || 0);
                return chapterOrder === 'old-new' ? dateA - dateB : dateB - dateA;
              })
              .map(chapter => renderChapterNav(chapter))}
          </div>
        </div>
      
      {/* Sidebar Toggle Button for Study Mode */}
      {isStudyMode && (
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{
            position: 'fixed',
            left: isSidebarOpen ? '320px' : '0',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: isSidebarOpen ? '0 8px 8px 0' : '0 8px 8px 0',
            padding: '16px 8px',
            cursor: 'pointer',
            fontSize: '20px',
            zIndex: 1001,
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
            transition: 'left 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px'
          }}
        >
          {isSidebarOpen ? '◀' : '▶'}
        </button>
      )}

      {/* Study Mode Control Buttons - Fixed Position Top Right */}
      {isStudyMode && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          display: 'flex',
          gap: '8px',
          zIndex: 1001
        }}>
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            style={{
              padding: '10px 14px',
              background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              color: isDarkMode ? '#e2e8f0' : '#2d3748',
              cursor: 'pointer',
              fontSize: '18px',
              lineHeight: '1',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
            }}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          <button
            onClick={() => setIsStudyMode(false)}
            style={{
              padding: '10px 14px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: '1',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
            }}
            title="Exit Study Mode"
          >
            📚 Exit Study
          </button>
        </div>
      )}

      {/* Overlay for Study Mode */}
      {isStudyMode && isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: '0',
            left: '320px',
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 998,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Main Content Area */}
      <div ref={contentRef} style={{
        flex: 1,
        overflow: 'auto',
        padding: isStudyMode ? '0' : '32px',
        background: isStudyMode ? (isDarkMode ? '#1a202c' : '#ffffff') : '#f7fafc',
        position: 'relative',
        marginLeft: isStudyMode ? '0' : '0'
      }}>
        {/* Study Mode Hint Banner - shown when user exits study mode */}
        {!isStudyMode && showStudyModeHint && (
          <div style={{
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: '500px',
            background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
            color: '#fff',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(72, 187, 120, 0.3)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideDown 0.3s ease-out'
          }}>
            <span style={{ fontSize: '24px' }}>💡</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
                Study Mode exited
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
                Click "🎯 Study Mode" in the sidebar to re-enter focus mode
              </p>
            </div>
            <button
              onClick={() => setShowStudyModeHint(false)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                cursor: 'pointer',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            >
              ×
            </button>
          </div>
        )}

        {error && (
          <div className="error-message" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {selectedAsset ? (
          <div style={{
            maxWidth: isStudyMode ? '1170px' : 'none',
            margin: isStudyMode ? '0 auto' : '0',
            padding: isStudyMode ? '40px 32px' : '0'
          }}>
            {/* Asset Title and Metadata */}
            <div style={{ marginBottom: '24px' }}>
              {/* Chapter/Lesson Badge */}
              {selectedAsset.chapter && (
                <div style={{ marginBottom: '12px' }}>
                  <span style={{
                    padding: '6px 12px',
                    background: isStudyMode 
                      ? (isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.1)') 
                      : 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: `1px solid ${isStudyMode ? (isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)') : 'rgba(102, 126, 234, 0.2)'}`
                  }}>
                    📖 {typeof selectedAsset.chapter === 'object' && selectedAsset.chapter.title
                      ? selectedAsset.chapter.title 
                      : getChapterName(selectedAsset.chapter) || 'Course Material'}
                  </span>
                </div>
              )}
              
              <h1 style={{ 
                fontSize: isStudyMode ? '32px' : '28px', 
                fontWeight: '700', 
                color: isStudyMode ? (isDarkMode ? '#e2e8f0' : '#1a202c') : '#1a202c', 
                marginBottom: '8px',
                lineHeight: '1.2',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <span>{selectedAsset.title}</span>
                {selectedAsset.assetType === 'video' && getVideoDuration(selectedAsset._id) && (
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    padding: '6px 12px',
                    background: isStudyMode ? (isDarkMode ? 'rgba(66, 153, 225, 0.2)' : 'rgba(66, 153, 225, 0.1)') : 'rgba(66, 153, 225, 0.1)',
                    color: '#4299e1',
                    borderRadius: '8px',
                    border: `1px solid ${isStudyMode ? (isDarkMode ? 'rgba(66, 153, 225, 0.3)' : 'rgba(66, 153, 225, 0.2)') : 'rgba(66, 153, 225, 0.2)'}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <AccessTimeIcon sx={{ fontSize: '16px' }} /> {formatDuration(getVideoDuration(selectedAsset._id))}
                  </span>
                )}
              </h1>
              {selectedAsset.description && (
                <p style={{ 
                  fontSize: '15px', 
                  color: isStudyMode ? (isDarkMode ? '#a0aec0' : '#718096') : '#718096', 
                  marginBottom: '12px',
                  lineHeight: '1.6'
                }}>
                  {selectedAsset.description}
                </p>
              )}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{
                  padding: '4px 10px',
                  background: isStudyMode ? (isDarkMode ? '#2d3748' : '#edf2f7') : '#edf2f7',
                  color: isStudyMode ? (isDarkMode ? '#a0aec0' : '#718096') : '#718096',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {selectedAsset.assetType === 'note' ? 'Reading Material' : 
                   selectedAsset.assetType === 'document' ? 'Document' :
                   selectedAsset.assetType === 'video' ? 'Video' :
                   selectedAsset.assetType === 'quiz' ? 'Quiz' :
                   selectedAsset.assetType === 'flashcard' ? 'Flashcard Set' :
                   selectedAsset.assetType === 'interview' ? 'Interview Practice' :
                   selectedAsset.assetType === 'link' ? 'External Link' :
                   selectedAsset.assetType}
                </span>
                {selectedAsset.isRequired && (
                  <span style={{
                    padding: '4px 10px',
                    background: '#f56565',
                    color: '#fff',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Required
                  </span>
                )}
                {isAssetCompleted(selectedAsset._id) && (
                  <span style={{
                    padding: '4px 10px',
                    background: '#48bb78',
                    color: '#fff',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    ✓ Completed
                  </span>
                )}
              </div>
            </div>

            {/* Asset Content */}
            <AssetRenderer 
              asset={selectedAsset}
              onComplete={handleAssetComplete}
              isCompleted={isAssetCompleted(selectedAsset._id)}
              isDarkMode={isStudyMode && isDarkMode}
              onInteraction={() => setIsChatbotOpen(false)}
            />

            {/* Study Mode Navigation */}
            {isStudyMode && (hasPreviousAsset() || hasNextAsset()) && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '40px',
                paddingTop: '24px',
                borderTop: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`
              }}>
                {hasPreviousAsset() ? (
                  <button
                    onClick={navigateToPreviousAsset}
                    style={{
                      padding: '12px 24px',
                      background: isDarkMode ? '#2d3748' : '#ffffff',
                      border: `2px solid ${isDarkMode ? '#4a5568' : '#667eea'}`,
                      color: isDarkMode ? '#e2e8f0' : '#667eea',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    ← Previous Lesson
                  </button>
                ) : (
                  <div />
                )}

                <div style={{ 
                  fontSize: '14px', 
                  color: isDarkMode ? '#a0aec0' : '#718096',
                  fontWeight: '600'
                }}>
                  Lesson {getCurrentAssetIndex() + 1} of {orderedAssets.length}
                </div>

                {hasNextAsset() ? (
                  <button
                    onClick={navigateToNextAsset}
                    style={{
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      color: '#ffffff',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    Next Lesson →
                  </button>
                ) : (
                  <div />
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '80px 20px',
            color: isStudyMode ? (isDarkMode ? '#a0aec0' : '#4a5568') : '#4a5568'
          }}>
            <p style={{ fontSize: '48px', marginBottom: '16px' }}>📚</p>
            <h3 style={{ fontSize: '20px', marginBottom: '8px', color: isStudyMode ? (isDarkMode ? '#e2e8f0' : '#1a202c') : '#1a202c' }}>
              Welcome to {course.title}
            </h3>
            {isStudyMode ? (
              <>
                <p style={{ marginBottom: '16px' }}>Exit study mode to browse and select lessons</p>
                <button
                  onClick={() => setIsStudyMode(false)}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  📚 Browse Course Content
                </button>
              </>
            ) : (
              <p>Select a lesson from the sidebar to get started</p>
            )}
          </div>
        )}
      </div>

      {/* AI Chat Assistant */}
      {selectedAsset && (
        <AIChatAssistant 
          assetId={selectedAsset._id}
          chapterId={selectedAsset.chapter?._id || selectedAsset.chapter}
          isDarkMode={isStudyMode && isDarkMode}
          isOpen={isChatbotOpen}
          onOpenChange={setIsChatbotOpen}
        />
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            background: toast.type === 'success' ? '#48bb78' : '#f56565',
            color: '#ffffff',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            zIndex: 10000,
            fontSize: '15px',
            fontWeight: '500',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          <span style={{ fontSize: '20px' }}>
            {toast.type === 'success' ? '✓' : '✕'}
          </span>
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default CourseContent;
